export const METHOD_LABELS = {
  square: "Card / Afterpay via Square",
  bank_transfer: "Bank Transfer",
  manual: "Manual Payment",
  stripe: "Card",
  paypal: "PayPal",
};

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric", timeZone: "Australia/Sydney" }) : "—";
export const money = (n) => `$${(n ?? 0).toFixed(2)}`;

export const InvoicePaper = ({ inv }) => {
  const o = inv.order;
  const b = inv.business;
  const methodLabel = METHOD_LABELS[o.payment_method] || o.payment_method;

  return (
    <div className="invoice-paper relative max-w-3xl mx-auto bg-white border border-[#C5A059]/30 shadow-sm px-8 md:px-14 py-12 md:py-16"
      data-testid="invoice-paper">
      <div className="flex flex-wrap justify-between gap-6 pb-10 border-b border-[#C5A059]/25">
        <div>
          <img src="/assets/logo.png" alt="Australian Luxe" className="w-14 h-14 rounded-full mb-4" />
          <p className="font-serif text-2xl font-light tracking-wide">Australian Luxe</p>
          <div className="text-xs text-[#4A4A4A] mt-2 space-y-0.5">
            {b.abn && <p>ABN {b.abn}</p>}
            {b.address && <p className="whitespace-pre-line">{b.address}</p>}
            {b.phone && <p>{b.phone}</p>}
            {b.email && <p>{b.email}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="font-serif text-4xl font-light text-[#9E7B3A]">Tax Invoice</p>
          <p className="text-sm mt-3 font-medium" data-testid="invoice-number">{inv.invoice_number}</p>
          <p className="text-xs text-[#4A4A4A] mt-1">Issued {fmtDate(inv.invoice_date)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 py-8 border-b border-[#C5A059]/25 text-sm">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-2">Billed To</p>
          <p className="font-medium" data-testid="invoice-customer-name">{o.customer?.name}</p>
          {o.customer?.email && <p className="text-[#4A4A4A]">{o.customer.email}</p>}
          {o.customer?.phone && <p className="text-[#4A4A4A]">{o.customer.phone}</p>}
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E7B3A] mb-2">Order Reference</p>
          <p className="font-medium" data-testid="invoice-order-ref">{o.order_number}</p>
          {inv.paid_at && <p className="text-xs text-[#4A4A4A] mt-1">Payment received {fmtDate(inv.paid_at)}</p>}
        </div>
      </div>

      <table className="w-full text-sm mt-8" data-testid="invoice-items">
        <thead>
          <tr className="border-b border-[#C5A059]/30 text-left">
            <th className="pb-3 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium">Description</th>
            <th className="pb-3 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium text-center w-16">Qty</th>
            <th className="pb-3 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium text-right w-28">Unit Price</th>
            <th className="pb-3 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium text-right w-28">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#C5A059]/10">
          {o.items?.map((i, idx) => (
            <tr key={idx}>
              <td className="py-3.5 pr-4">{i.name}</td>
              <td className="py-3.5 text-center text-[#4A4A4A]">{i.qty}</td>
              <td className="py-3.5 text-right text-[#4A4A4A]">{money(i.price)}</td>
              <td className="py-3.5 text-right">{money(i.qty * i.price)}</td>
            </tr>
          ))}
          {o.shipping > 0 && (
            <tr>
              <td className="py-3.5 pr-4">Express Shipping</td>
              <td className="py-3.5 text-center text-[#4A4A4A]">1</td>
              <td className="py-3.5 text-right text-[#4A4A4A]">{money(o.shipping)}</td>
              <td className="py-3.5 text-right">{money(o.shipping)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="relative flex justify-end mt-6">
        {inv.paid && (
          <div className="absolute left-0 md:left-4 top-1/2 -translate-y-1/2 -rotate-12 border-[3px] border-[#C5A059] text-[#9E7B3A] px-6 py-2 text-xl tracking-[0.4em] font-medium uppercase"
            data-testid="invoice-paid-stamp">
            Paid
          </div>
        )}
        <div className="w-72 text-sm space-y-2" data-testid="invoice-totals">
          <div className="flex justify-between text-[#4A4A4A]">
            <span>Subtotal (ex GST)</span><span data-testid="invoice-ex-gst">{money(inv.totals.ex_gst)}</span>
          </div>
          <div className="flex justify-between text-[#4A4A4A]">
            <span>GST (10%)</span><span data-testid="invoice-gst">{money(inv.totals.gst)}</span>
          </div>
          <div className="flex justify-between font-medium text-base border-t border-[#C5A059]/30 pt-3">
            <span>Total (AUD)</span><span data-testid="invoice-total">{money(inv.totals.total)}</span>
          </div>
        </div>
      </div>

      <div className="mt-10 border border-[#C5A059]/30 bg-[#C5A059]/5 px-6 py-4 text-sm" data-testid="invoice-payment-status">
        {inv.paid ? (
          <p>
            <span className="font-medium text-[#9E7B3A] tracking-widest uppercase text-xs">Paid in full</span>
            <span className="text-[#4A4A4A]"> — {methodLabel}{inv.payment_reference ? ` · Ref ${inv.payment_reference}` : ""}{inv.paid_at ? ` · ${fmtDate(inv.paid_at)}` : ""}</span>
          </p>
        ) : (
          <p><span className="font-medium text-amber-700 tracking-widest uppercase text-xs">Awaiting payment</span>
            <span className="text-[#4A4A4A]"> — {methodLabel}</span></p>
        )}
      </div>

      <p className="text-center text-xs text-[#4A4A4A] mt-12 tracking-[0.2em] uppercase">
        Thank you for choosing Australian Luxe
      </p>
    </div>
  );
};
